import { useState } from "react";
import { ChevronDown, Cloud, CloudRain, Sun, CloudSun, Info } from "lucide-react";
import { fmtDate } from "@/lib/api";
import { goingBand, goingRating, railText, railOut, goingStick } from "@/lib/raceFormat";

const TINT = {
  firm: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  good: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  soft: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  heavy: "bg-rose-500/15 text-rose-300 border-rose-500/40",
};
const SEG = ["#10B981", "#22C55E", "#84CC16", "#A3B81A", "#EAB308", "#F59E0B", "#F97316", "#EF4444", "#DC2626", "#B91C1C"];

const WeatherIcon = ({ w }) => {
  const k = (w || "").toLowerCase();
  const I = /rain|shower|storm/.test(k) ? CloudRain : /fine|sunny|clear/.test(k) ? Sun : /partly|mostly/.test(k) ? CloudSun : Cloud;
  return <I className="w-4 h-4 text-slate-400" aria-hidden />;
};

const GoingScale = ({ rating }) => (
  <div className="w-full max-w-sm mt-3" data-testid="going-scale">
    <div className="grid grid-cols-10 gap-1">
      {SEG.map((c, i) => (
        <span key={i} data-testid={`going-seg-${i + 1}`} style={{ backgroundColor: c }}
          className={`h-2 rounded-sm transition-opacity ${rating === i + 1 ? "ring-2 ring-white ring-offset-2 ring-offset-[#0B101D] opacity-100" : "opacity-40"}`} />
      ))}
    </div>
    <div className="grid grid-cols-10 gap-1 mt-1.5 text-[11px] text-slate-500">
      <span className="col-span-2">Firm</span><span className="col-span-2">Good</span><span className="col-span-3">Soft</span><span className="col-span-3">Heavy</span>
    </div>
  </div>
);

const TrackDetails = ({ m }) => {
  const [open, setOpen] = useState(false);
  const gs = goingStick(m.track_info || "");
  const rows = [["Penetrometer", m.penetrometer], ["Going stick index", gs.index], ["Penetration", gs.penetration], ["Shear", gs.shear]].filter(([, v]) => v);
  if (!rows.length) return null;
  return (
    <div className="mt-4 surface overflow-hidden" data-testid="track-details">
      <button data-testid="track-details-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-slate-200 hover:bg-[#1B2640]/60 transition-colors">
        Track details<ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <dl className="divide-y divide-[#202E4C] border-t border-[#202E4C]" data-testid="track-details-rows">
          {rows.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between px-4 py-2.5 text-sm">
              <dt className="text-slate-400">{k}</dt><dd className="font-mono text-slate-100" data-testid={`track-detail-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
};

const BiasNote = ({ rail, onTips }) => {
  const out = railOut(rail || "");
  if (!out || Number(out) === 0) return null;
  return (
    <div className="mt-4 flex gap-3 rounded-lg border border-blue-500/30 bg-blue-500/10 px-4 py-3" data-testid="track-bias-note">
      <Info className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
      <p className="text-sm text-slate-200">Rail out {out}m: wider runners may find better ground.{" "}
        <button data-testid="bias-see-tips" onClick={onTips} className="text-blue-400 font-semibold hover:underline">See our tips</button>
      </p>
    </div>
  );
};

export const MeetingHeader = ({ m, onTips }) => {
  const band = goingBand(m.track_condition || "");
  const surface = m.track_type && !/turf|grass/i.test(m.track_type) ? m.track_type : null;
  return (
    <div className="mb-6" data-testid="meeting-track-details">
      <p className="eyebrow">{m.state} · {fmtDate(m.date)}</p>
      <h1 className="page-title" data-testid="meeting-title">{m.track}</h1>
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        {m.track_condition && <span data-testid="meeting-condition-chip" className={`px-3 py-1 rounded-full border text-sm font-bold ${TINT[band] || "bg-blue-500/15 text-blue-300 border-blue-500/40"}`}>{m.track_condition}</span>}
        {m.rail && <span className="text-sm text-slate-300" data-testid="meeting-rail">{railText(m.rail)}</span>}
        {m.weather && <span className="inline-flex items-center gap-1.5 text-sm text-slate-300" data-testid="meeting-weather"><WeatherIcon w={m.weather} />{m.weather}</span>}
        {surface && <span className="text-sm text-slate-400" data-testid="meeting-track-type">{surface} surface</span>}
      </div>
      {goingRating(m.track_condition || "") && <GoingScale rating={goingRating(m.track_condition)} />}
      <BiasNote rail={m.rail} onTips={onTips} />
      <TrackDetails m={m} />
    </div>
  );
};
